// Conditional analytics provider boot.
//
// Wires `posthog-js` into the existing `setAnalytics` seam from
// src/utils/analytics.js. Conditional on VITE_POSTHOG_KEY so a build
// without the key does not pay the bytes (the import is dynamic).
//
// Env:
//   VITE_POSTHOG_KEY   — required to enable analytics. Without it
//                        every track() call stays a no-op.
//   VITE_POSTHOG_HOST  — optional, defaults to https://us.i.posthog.com.
//
// Privacy notes:
//   - `person_profiles: 'identified_only'` skips creating a profile
//     for anonymous browsers; only authenticated users get tracked.
//     Combined with `capture_pageview: false` this keeps the default
//     footprint tiny (no auto-collected IP / fingerprint events).
//   - `disable_session_recording: true` hard-rules-out the heavy
//     session-replay product. Be Bold deals with intimate health
//     content; recording the screen would be inappropriate.
//   - User id is the Supabase auth user id (UUID), no email or PII.
//     Wire `identify()` from AppContext when the auth state lands.

import { setAnalytics } from '@/utils/analytics';

let booted = false;

export async function initAnalytics() {
  if (booted) return;
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return;
  booted = true;
  try {
    const { default: posthog } = await import('posthog-js');
    posthog.init(key, {
      api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: false,
      disable_session_recording: true,
      autocapture: false,
    });
    setAnalytics({
      track: (name, props) => posthog.capture(name, props || {}),
      identify: (id, traits) => posthog.identify(id, traits || {}),
    });
  } catch (err) {
    // Don't let an analytics boot error mask the actual app boot.
    console.warn('[analytics] PostHog init failed', err);
  }
}
